<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useUserStore } from '@/store/user-store';
import { useRouter } from 'vue-router';
import Chart from "primevue/chart";

const props = defineProps({
  portfolioId: {
    type: [String, Number],
    default: null
  },
  portfolioAnalytics: {
    type: Object,
    default: () => ({})
  },
  setSection: {
    type: Function,
    default: null
  }
});

const emit = defineEmits(['attribution-loaded']);

const store = useUserStore();
const router = useRouter();
const attributionData = ref(null);
const isLoading = ref(false);

const stackedBarData = ref({});
const stackedBarOptions = ref({});

// Cache management (similar to SectorBenchmarkChart)
const CACHE_KEY = 'portfolio_attribution_data';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Register onMounted hook immediately - before any other code that might cause async operations
onMounted(() => {
  // Load data if portfolio analytics is already available (same pattern as SectorBenchmarkChart)
  if (props.portfolioAnalytics && Object.keys(props.portfolioAnalytics).length > 0 && props.portfolioId) {
    setTimeout(() => {
      loadAttributionData();
    }, 3000);
  }
});

// Cache utilities
const getCachedData = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading attribution cache:', error);
    return null;
  }
};

const setCachedData = (data) => {
  try {
    const cacheObject = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.error('Error setting attribution cache:', error);
  }
};

const isCacheExpired = (timestamp) => {
  return Date.now() - timestamp > CACHE_DURATION;
};

// Load attribution data with caching (similar to SectorBenchmarkChart pattern)
const loadAttributionData = async () => {
  if (isLoading.value || !props.portfolioId) return;

  isLoading.value = true;

  try {
    // Create cache key with portfolioId for user-specific caching
    const portfolioCacheKey = `${CACHE_KEY}_${props.portfolioId}`;

    // Check cache first
    const cached = localStorage.getItem(portfolioCacheKey);
    const cachedData = cached ? JSON.parse(cached) : null;

    if (cachedData && !isCacheExpired(cachedData.timestamp)) {
      console.log('Using cached attribution data for portfolio:', props.portfolioId);
      attributionData.value = cachedData.data;
      setupStackedBar();
      isLoading.value = false;
      return;
    }

    console.log('Fetching fresh attribution data for portfolio:', props.portfolioId);
    const data = await store.getPortfolioAttributionData(props.portfolioId);
    console.log('Fetched attribution data:', data);

    // Handle response data structure - attribution data is nested under responseData.attributionData
    if (data && data.responseData) {
      // Check if attribution data is empty string (queued state)
      if (data.responseData.attributionData === "" || data.responseData.attributionData === null || data.responseData.attributionData === undefined) {
        console.log('Attribution data is empty, setting to queued state');
        attributionData.value = "queued";
        isLoading.value = false;
        return;
      }

      if (data.responseData.attributionData) {
        attributionData.value = data.responseData.attributionData;
      } else {
        attributionData.value = data.responseData;
      }
    } else {
      attributionData.value = data;
    }

    // Cache with portfolio-specific key
    const cacheObject = {
      data: attributionData.value,
      timestamp: Date.now()
    };
    localStorage.setItem(portfolioCacheKey, JSON.stringify(cacheObject));

    setupStackedBar();

    // Emit attribution loaded event
    emit('attribution-loaded');
  } catch (error) {
    console.error('Error loading attribution data:', error);
    // Show error state if needed
  } finally {
    isLoading.value = false;
  }
};

// Watch for portfolio analytics changes (similar to SectorBenchmarkChart watching sectorExposure)
watch(() => props.portfolioAnalytics, (newValue) => {
  if (newValue && Object.keys(newValue).length > 0 && !attributionData.value && props.portfolioId) {
    // Delay loading to avoid blocking initial render
    nextTick(() => {
      setTimeout(() => {
        loadAttributionData();
      }, 1500); // 1.5 second delay for better UX (same as SectorBenchmarkChart)
    });
  }
}, { immediate: true });

// Watch for portfolioId changes
watch([() => props.portfolioId, () => props.portfolioAnalytics], ([newPortfolioId, newAnalytics]) => {
  if (newPortfolioId && newAnalytics && Object.keys(newAnalytics).length > 0 && attributionData.value) {
    // Reload data if portfolio changes and we already have data
    loadAttributionData();
  }
});



// Navigate to details page
const navigateToDetails = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  if (props.setSection) {
    // Use setSection pattern when called from parent component
    props.setSection(2);
  } else {
    // Fallback to router navigation when used standalone
    router.push({
      path: '/sector-analytics-detail',
      query: { portfolioId: props.portfolioId }
    });
  }
};

// Setup stacked attribution bar
const setupStackedBar = () => {
  if (!attributionData.value || attributionData.value === 'queued' || !attributionData.value?.summary) return;

  const summary = attributionData.value.summary;

  stackedBarData.value = {
    labels: ['Attribution Breakdown'],
    datasets: [
      {
        label: 'Allocation',
        data: [summary.allocation_total],
        backgroundColor: '#28a745',
        borderWidth: 0,
      },
      {
        label: 'Selection',
        data: [summary.selection_total],
        backgroundColor: summary.selection_total >= 0 ? '#ffc107' : '#fd7e14',
        borderWidth: 0,
      },
      {
        label: 'Interaction',
        data: [summary.interaction_total],
        backgroundColor: summary.interaction_total >= 0 ? '#17a2b8' : '#6f42c1',
        borderWidth: 0,
      }
    ]
  };

  stackedBarOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        grid: {
          display: true,
          color: '#e9ecef',
        },
        ticks: {
          callback: function(value) {
            return value.toFixed(2) + '%';
          }
        }
      },
      y: {
        stacked: true,
        display: false,
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: 600
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label;
            const value = context.parsed.x;
            let definition = '';

            switch(label) {
              case 'Allocation':
                definition = 'Asset allocation decisions vs benchmark';
                break;
              case 'Selection':
                definition = 'Security selection within sectors';
                break;
              case 'Interaction':
                definition = 'Combined effect of allocation and selection';
                break;
            }

            return `${label}: ${value.toFixed(2)}% - ${definition}`;
          }
        }
      }
    }
  };
};

// Check attribution closure (within ±0.01% tolerance)
const isAttributionClosed = computed(() => {
  if (!attributionData.value || attributionData.value === 'queued' || !attributionData.value?.summary) return false;
  const summary = attributionData.value.summary;
  const difference = Math.abs(summary.excess_return - summary.sum_of_effects);
  return difference <= 0.01;
});

// Format percentage display
const formatPercentage = (value, showSign = true) => {
  if (value === null || value === undefined) return '0.00%';
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

// Get current month-year
const getCurrentMonthYear = () => {
  const date = new Date();
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Generate attribution microcopy
const getAttributionMicrocopy = () => {
  if (!attributionData.value || attributionData.value === 'queued' || !attributionData.value?.summary) return '';

  const summary = attributionData.value.summary;
  return `Your active return of ${formatPercentage(summary.excess_return)} comes from ${formatPercentage(summary.allocation_total)} Allocation, ${formatPercentage(summary.selection_total)} Selection, ${formatPercentage(summary.interaction_total)} Interaction.`;
};

// Retry function for queued attribution data
const retryAttributionData = () => {
  // Clear cache for this portfolio to force fresh API call
  const portfolioCacheKey = `${CACHE_KEY}_${props.portfolioId}`;
  localStorage.removeItem(portfolioCacheKey);

  // Clear store's portfolio attribution cache as well
  store.portfolioAttributionData = null;

  // Reset attribution data to trigger reload
  attributionData.value = null;

  // Load fresh data
  loadAttributionData();
};
</script>

<template>
  <div class="attribution-overview-card input-shadow br-10 p-20">
    <!-- Hero Panel Header - Always visible -->
    <div class="hero-header">
      <div class="header-content">
        <h2 class="hero-title">Monthly Sector Analysis</h2>
        <div class="header-right">
          <span class="month-year">{{ getCurrentMonthYear() }}</span>
        </div>
      </div>
    </div>

    <!-- Loading State - Show while getPortfolioAttributionData is fetching -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading sector attribution data...</p>
      </div>
    </div>

    <!-- Placeholder State - Show when waiting for data to load -->
    <div v-else-if="!attributionData" class="placeholder-state">
      <div class="placeholder-content">
        <div class="placeholder-icon">📊</div>
        <h4 class="placeholder-title">Sector Attribution Analysis</h4>
        <p class="placeholder-message">
          Attribution data will load automatically
        </p>
      </div>
    </div>

    <!-- Queued State - Show when attribution is being processed -->
    <div v-else-if="attributionData === 'queued'" class="queued-state">
      <div class="queued-content">
        <div class="queued-icon-container">
          <div class="queued-icon">⏳</div>
          <div class="pulse-ring"></div>
        </div>
        <h4 class="queued-title">Portfolio Attribution Analysis is in queue</h4>
        <div class="animated-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
        <p class="queued-message">
          Your portfolio attribution is being calculated. This usually takes a few moments.
        </p>
        <button class="retry-button" @click="retryAttributionData" :disabled="isLoading">
          <span v-if="!isLoading">🔄 Retry</span>
          <span v-else>Loading...</span>
        </button>
      </div>
    </div>

    <!-- Main Content - Only show after getPortfolioAttributionData succeeds -->
    <div v-else-if="attributionData && attributionData !== 'queued'" class="attribution-content">
      <!-- KPI Cards Section -->
      <div class="kpi-section">
        <div class="kpi-cards-grid">
          <!-- Portfolio Return -->
          <div class="kpi-card portfolio-return">
            <div class="kpi-label">Portfolio Return</div>
            <div class="kpi-value text-primary">
              {{ formatPercentage(attributionData.summary?.portfolio_total_return) }}
            </div>
          </div>

          <!-- Benchmark Return -->
          <div class="kpi-card benchmark-return">
            <div class="kpi-label">Benchmark Return</div>
            <div class="kpi-value text-muted">
              {{ formatPercentage(attributionData.summary?.benchmark_total_return) }}
            </div>
          </div>

          <!-- Excess Return -->
          <div class="kpi-card excess-return">
            <div class="kpi-label">Excess Return</div>
            <div class="kpi-value text-success">
              {{ formatPercentage(attributionData.summary?.excess_return) }}
            </div>
          </div>

          <!-- Attribution Closure -->
          <div class="kpi-card attribution-closure">
            <div class="kpi-label-with-tooltip">
              <span class="kpi-label">Attribution Closure</span>
              <div class="tooltip-container">
                <span class="info-icon">ℹ️</span>
                <div class="tooltip-content">
                  <strong>Attribution Closure:</strong> Excess return equals allocation + selection + interaction.
                  <br>Current difference: {{ Math.abs((attributionData.summary?.excess_return || 0) - (attributionData.summary?.sum_of_effects || 0)).toFixed(3) }}%
                </div>
              </div>
            </div>
            <div class="kpi-value closure-status">
              <span v-if="isAttributionClosed" class="closure-check text-success">
                ✓ Closed
              </span>
              <span v-else class="closure-warning text-warning">
                ⚠ Check
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Stacked Attribution Bar Section -->
      <div class="attribution-bar-section">
        <h3 class="section-title">Attribution Breakdown</h3>

        <div class="stacked-bar-container">
          <Chart
            type="bar"
            :data="stackedBarData"
            :options="stackedBarOptions"
            class="attribution-chart"
          />
        </div>

        <!-- Microcopy -->
        <div class="attribution-microcopy">
          <p class="microcopy-text">{{ getAttributionMicrocopy() }}</p>
        </div>
      </div>

      <!-- See Details Button -->
      <div class="details-button-section">
        <button class="details-button" @click="navigateToDetails">
          <span>See Details</span>
          <span class="button-arrow">→</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.attribution-overview-card {
  background: white;
  min-height: 400px;
}

/* Hero Header */
.hero-header {
  margin-bottom: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hero-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0;
}

.month-year {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--an-text-muted);
}

/* KPI Cards */
.kpi-section {
  margin-bottom: 30px;
}

.kpi-cards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.kpi-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 12px;
  text-align: center;
  transition: all 0.2s ease;
  position: relative;
}

.kpi-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.kpi-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--an-text-muted);
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-label-with-tooltip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 3px;
}

.kpi-value {
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 1.2rem;
}

.closure-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  position: relative;
  min-height: 1.2rem;
  margin-top: -8px;
}

.closure-check,
.closure-warning {
  font-size: 1rem;
  font-weight: 700;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.info-icon {
  cursor: help;
  font-size: 12px;
  opacity: 0.7;
  transition: opacity 0.2s;
  transform: translateY(-1px);
}

.info-icon:hover {
  opacity: 1;
}

.tooltip-content {
  visibility: hidden;
  width: 280px;
  background-color: #333;
  color: white;
  text-align: left;
  border-radius: 4px;
  padding: 12px;
  position: absolute;
  z-index: 1000;
  bottom: 125%;
  left: 50%;
  margin-left: -140px;
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.tooltip-content::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

.tooltip-container:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}

/* Attribution Bar Section */
.attribution-bar-section {
  margin-bottom: 25px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 15px;
}

.stacked-bar-container {
  height: 100px;
  margin-bottom: 15px;
}

.attribution-chart {
  height: 100%;
  width: 100%;
}

.attribution-microcopy {
  text-align: center;
  padding: 12px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.microcopy-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0;
  line-height: 1.4;
}

/* Details Button */
.details-button-section {
  text-align: center;
}

.details-button {
  background: linear-gradient(135deg, var(--an-primary) 0%, #4A90E2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.details-button:hover {
  background: linear-gradient(135deg, #4A90E2 0%, var(--an-primary) 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(74, 144, 226, 0.3);
}

.button-arrow {
  font-size: 1.1rem;
  transition: transform 0.2s ease;
}

.details-button:hover .button-arrow {
  transform: translateX(3px);
}

/* Loading and Placeholder States */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid var(--an-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 14px;
  color: var(--an-text-muted);
  margin: 0;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Placeholder State */
.placeholder-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.placeholder-content {
  text-align: center;
}

.placeholder-icon {
  font-size: 2rem;
  margin-bottom: 8px;
}

.placeholder-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.placeholder-message {
  font-size: 0.9rem;
  color: var(--an-text-muted);
  margin: 0;
}

/* Queued State */
.queued-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.queued-content {
  text-align: center;
  max-width: 400px;
}

.queued-icon-container {
  position: relative;
  display: inline-block;
  margin-bottom: 20px;
}

.queued-icon {
  font-size: 3rem;
  position: relative;
  z-index: 2;
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border: 2px solid var(--an-primary);
  border-radius: 50%;
  opacity: 0;
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(0.5);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

.queued-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 15px 0;
}

.animated-dots {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin: 15px 0;
}

.dot {
  width: 8px;
  height: 8px;
  background: var(--an-primary);
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}

.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }
.dot:nth-child(3) { animation-delay: 0s; }

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

.queued-message {
  font-size: 0.95rem;
  color: var(--an-text-muted);
  margin: 0 0 20px 0;
  line-height: 1.4;
}

.retry-button {
  background: linear-gradient(135deg, var(--an-primary) 0%, #4A90E2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.retry-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #4A90E2 0%, var(--an-primary) 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

.retry-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .kpi-cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .kpi-cards-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .hero-title {
    font-size: 1.3rem;
  }

  .kpi-value {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .attribution-overview-card {
    padding: 15px;
  }

  .kpi-card {
    padding: 15px;
  }

  .details-button {
    padding: 10px 25px;
    font-size: 0.9rem;
  }
}
</style>
